import { z } from 'zod';
import { UserSummary } from './admin';
import { Id, IsoDate, PaginationQuery, paginated, queryArray } from './common';
import { OutcomeMetric } from './opportunities';
import { ExperimentStatus, OpportunityStatus, Verdict } from './transitions';

/**
 * Contrats des expériences et des résultats produit (`api.md` § Expériences et résultats, fiche
 * T26). Les charges `jsonb` (`keywords`, `thresholds`, `results`, `qualitative`) gardent leurs
 * clés `snake_case` telles que stockées (`data-model.md` § `experiments`) ; les colonnes suivent
 * la convention `camelCase` de l'API.
 */

// ---------------------------------------------------------------------------
// Charges jsonb
// ---------------------------------------------------------------------------

export const MatchType = z.enum(['exact', 'phrase', 'broad']);
export type MatchType = z.infer<typeof MatchType>;

export const EXPERIMENT_KEYWORD_MAX_LENGTH = 200;
export const EXPERIMENT_KEYWORDS_MAX = 50;
export const EXPERIMENT_NAME_MAX_LENGTH = 200;
export const EXPERIMENT_TEXT_MAX_LENGTH = 2000;
export const EXPERIMENT_NOTES_MAX_LENGTH = 4000;
export const VERDICT_RATIONALE_MIN_LENGTH = 20;
export const EXPERIMENT_BUDGET_MAX_USD = 100_000;
export const QUALITATIVE_ITEMS_MAX = 30;
export const OUTCOME_METRICS_MAX = 20;

/** Élément de `experiments.keywords` : requête ciblée, sa correspondance et son CPC attendu. */
export const ExperimentKeyword = z.object({
  text: z.string().trim().min(1).max(EXPERIMENT_KEYWORD_MAX_LENGTH),
  match_type: MatchType,
  expected_cpc_usd: z.number().min(0).nullable(),
  avg_monthly_searches: z.number().int().min(0).nullable(),
});
export type ExperimentKeyword = z.infer<typeof ExperimentKeyword>;

export const THRESHOLD_KEYS = [
  'min_impressions',
  'ctr_fail',
  'ctr_success',
  'cvr_fail',
  'cvr_success',
  'cpa_fail_multiplier',
  'cpa_success_multiplier',
  'strong_signals_min',
] as const;
export type ThresholdKey = (typeof THRESHOLD_KEYS)[number];

const Ratio = z.number().min(0).max(1);
const Multiplier = z.number().min(0).max(100);

/**
 * `experiments.thresholds` (`pilot.md` § Seuils d'expérience) : ratios en fraction (`0.02` = 2 %),
 * multiplicateurs du CPC attendu pour le CPA. Le seuil d'échec doit rester strictement plus
 * exigeant que le seuil de succès (`fail < success`, l'inverse pour le CPA).
 */
export const Thresholds = z
  .object({
    min_impressions: z.number().int().min(0),
    ctr_fail: Ratio,
    ctr_success: Ratio,
    cvr_fail: Ratio,
    cvr_success: Ratio,
    cpa_fail_multiplier: Multiplier,
    cpa_success_multiplier: Multiplier,
    strong_signals_min: z.number().int().min(0).max(1000),
  })
  .superRefine((value, ctx) => {
    if (value.ctr_fail >= value.ctr_success) {
      ctx.addIssue({
        code: 'custom',
        path: ['ctr_fail'],
        message: 'validation.thresholdFailBelowSuccess',
      });
    }
    if (value.cvr_fail >= value.cvr_success) {
      ctx.addIssue({
        code: 'custom',
        path: ['cvr_fail'],
        message: 'validation.thresholdFailBelowSuccess',
      });
    }
    if (value.cpa_fail_multiplier <= value.cpa_success_multiplier) {
      ctx.addIssue({
        code: 'custom',
        path: ['cpa_fail_multiplier'],
        message: 'validation.thresholdCpaFailAboveSuccess',
      });
    }
  });
export type Thresholds = z.infer<typeof Thresholds>;

export const STRONG_SIGNAL_KEYS = [
  'interviews',
  'preorders',
  'demo_requests',
  'deposits',
  'other',
] as const;
export type StrongSignalKey = (typeof STRONG_SIGNAL_KEYS)[number];

const SignalCount = z.number().int().min(0).max(1_000_000);

/** Signaux forts d'une expérience (`pilot.md` § Seuils) : entretien, précommande, démo, dépôt. */
export const StrongSignals = z.object({
  interviews: SignalCount,
  preorders: SignalCount,
  demo_requests: SignalCount,
  deposits: SignalCount,
  other: SignalCount,
});
export type StrongSignals = z.infer<typeof StrongSignals>;

const Counter = z.number().int().min(0).max(100_000_000);

/** Partie saisie de `experiments.results` : les dérivés sont recalculés par le serveur. */
export const ExperimentResultsInput = z
  .object({
    impressions: Counter,
    clicks: Counter,
    cost_usd: z.number().min(0).max(EXPERIMENT_BUDGET_MAX_USD),
    visits: Counter,
    engaged_visits: Counter,
    signups: Counter,
    strong_signals: StrongSignals,
  })
  .superRefine((value, ctx) => {
    if (value.clicks > value.impressions) {
      ctx.addIssue({
        code: 'custom',
        path: ['clicks'],
        message: 'validation.clicksBelowImpressions',
      });
    }
    if (value.engaged_visits > value.visits) {
      ctx.addIssue({
        code: 'custom',
        path: ['engaged_visits'],
        message: 'validation.engagedBelowVisits',
      });
    }
  });
export type ExperimentResultsInput = z.infer<typeof ExperimentResultsInput>;

/**
 * `experiments.results` tel que stocké et renvoyé : saisie + dérivés posés par le serveur
 * (`ctr`, `avg_cpc_usd`, `cpa_usd`) et `updated_at` (l'UI distingue une saisie partielle).
 */
export const ExperimentResults = z.object({
  impressions: z.number().int().min(0),
  clicks: z.number().int().min(0),
  ctr: z.number().nullable(),
  avg_cpc_usd: z.number().nullable(),
  cost_usd: z.number(),
  visits: z.number().int().min(0),
  engaged_visits: z.number().int().min(0),
  signups: z.number().int().min(0),
  strong_signals: StrongSignals,
  cpa_usd: z.number().nullable(),
  updated_at: IsoDate,
});
export type ExperimentResults = z.infer<typeof ExperimentResults>;

const QualitativeList = z
  .array(z.string().trim().min(1).max(EXPERIMENT_NOTES_MAX_LENGTH))
  .max(QUALITATIVE_ITEMS_MAX);

export const ExperimentQualitative = z.object({
  learnings: QualitativeList,
  quotes: QualitativeList,
  objections: QualitativeList,
});
export type ExperimentQualitative = z.infer<typeof ExperimentQualitative>;

/** Suggestion de verdict calculée par `computeVerdictSuggestion` (`packages/core`), jamais appliquée d'office. */
export const VerdictSuggestion = z.object({
  verdict: Verdict,
  ctr: z.number(),
  cvr: z.number(),
  cpa_usd: z.number().nullable(),
  strong: z.number(),
  /** Raisons lisibles (français), affichées telles quelles. */
  reasons: z.array(z.string()),
});
export type VerdictSuggestion = z.infer<typeof VerdictSuggestion>;

// ---------------------------------------------------------------------------
// Lectures
// ---------------------------------------------------------------------------

/** Opportunité portée par l'expérience (lien, badge de statut, marché, cluster pour l'ajout de mots-clés). */
export const ExperimentOpportunity = z.object({
  id: Id,
  title: z.string(),
  status: OpportunityStatus,
  marketId: z.string(),
  clusterId: Id.nullable(),
});
export type ExperimentOpportunity = z.infer<typeof ExperimentOpportunity>;

export const ExperimentRow = z.object({
  id: Id,
  name: z.string(),
  status: ExperimentStatus,
  channel: z.string(),
  opportunity: ExperimentOpportunity,
  budgetPlannedUsd: z.number(),
  budgetSpentUsd: z.number().nullable(),
  verdict: Verdict.nullable(),
  /** Dates prévues en `YYYY-MM-DD` (colonnes `date`). */
  plannedStart: z.string().nullable(),
  plannedEnd: z.string().nullable(),
  startedAt: IsoDate.nullable(),
  endedAt: IsoDate.nullable(),
  decidedAt: IsoDate.nullable(),
  /** Owner de l'opportunité (l'expérience n'en porte pas). */
  owner: UserSummary.nullable(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type ExperimentRow = z.infer<typeof ExperimentRow>;

export const ExperimentDetail = ExperimentRow.extend({
  hypothesis: z.string(),
  segment: z.string(),
  valueProposition: z.string(),
  landingUrl: z.string().nullable(),
  copyNotes: z.string().nullable(),
  keywords: z.array(ExperimentKeyword),
  thresholds: Thresholds,
  /** Seuils par défaut du radar (`pilot.md`), pour le bouton « Reprendre » de `ThresholdsEditor`. */
  suggestedThresholds: Thresholds,
  /** Moyenne des `expected_cpc_usd` connus (sert au CPA cible). */
  expectedCpcUsd: z.number().nullable(),
  results: ExperimentResults.nullable(),
  qualitative: ExperimentQualitative,
  verdictRationale: z.string().nullable(),
  decidedBy: UserSummary.nullable(),
  /** `null` tant qu'aucun résultat n'est saisi. */
  verdictSuggestion: VerdictSuggestion.nullable(),
  /** `true` dès `planned` : `thresholds`, `budgetPlannedUsd` et `keywords` sont figés. */
  locked: z.boolean(),
});
export type ExperimentDetail = z.infer<typeof ExperimentDetail>;

/** `GET /experiments` : filtres `status`, `opportunityId`, `ownerId` ; tri fixe `updatedAt desc`. */
export const ExperimentsQuery = PaginationQuery.extend({
  status: queryArray(ExperimentStatus).optional(),
  opportunityId: Id.optional(),
  ownerId: Id.optional(),
});
export type ExperimentsQuery = z.infer<typeof ExperimentsQuery>;
export type ExperimentsQueryInput = z.input<typeof ExperimentsQuery>;

export const ExperimentsResponse = paginated(ExperimentRow);
export type ExperimentsResponse = z.infer<typeof ExperimentsResponse>;

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const Name = z.string().trim().min(1).max(EXPERIMENT_NAME_MAX_LENGTH);
const LongText = z.string().trim().min(1).max(EXPERIMENT_TEXT_MAX_LENGTH);
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'validation.date' });

/** `POST /opportunities/:id/experiments` : tout est pré-rempli par `generateExperimentBrief`. */
export const ExperimentCreate = z.object({ name: Name.optional() });
export type ExperimentCreate = z.infer<typeof ExperimentCreate>;

/**
 * `PATCH /experiments/:id`. `thresholds`, `budgetPlannedUsd` et `keywords` sont refusés dès
 * `planned` (`409 experiment_locked`) ; le reste reste éditable jusqu'à `completed` inclus.
 */
export const ExperimentPatch = z
  .object({
    name: Name.optional(),
    hypothesis: LongText.optional(),
    segment: LongText.optional(),
    valueProposition: LongText.optional(),
    landingUrl: z.url().max(EXPERIMENT_TEXT_MAX_LENGTH).nullable().optional(),
    copyNotes: z.string().max(EXPERIMENT_NOTES_MAX_LENGTH).nullable().optional(),
    plannedStart: DateOnly.nullable().optional(),
    plannedEnd: DateOnly.nullable().optional(),
    keywords: z.array(ExperimentKeyword).min(1).max(EXPERIMENT_KEYWORDS_MAX).optional(),
    budgetPlannedUsd: z.number().positive().max(EXPERIMENT_BUDGET_MAX_USD).optional(),
    budgetSpentUsd: z.number().min(0).max(EXPERIMENT_BUDGET_MAX_USD).nullable().optional(),
    thresholds: Thresholds.optional(),
    qualitative: ExperimentQualitative.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'validation.emptyPatch' });
export type ExperimentPatch = z.infer<typeof ExperimentPatch>;
/** Nom du corps côté SPA (fiche T27). */
export const UpdateExperimentBody = ExperimentPatch;
export type UpdateExperimentBody = ExperimentPatch;

/** `POST /experiments/:id/status` ; `rationale` obligatoire pour `aborted`. */
export const ExperimentStatusChange = z.object({
  to: ExperimentStatus,
  rationale: z.string().trim().min(1).max(EXPERIMENT_TEXT_MAX_LENGTH).optional(),
});
export type ExperimentStatusChange = z.infer<typeof ExperimentStatusChange>;

/** `PUT /experiments/:id/results` : remplace `results` (et `qualitative` si fournie). */
export const ResultsPut = z.object({
  results: ExperimentResultsInput,
  qualitative: ExperimentQualitative.optional(),
});
export type ResultsPut = z.infer<typeof ResultsPut>;

/** `POST /experiments/:id/verdict` : rationale d'au moins 20 caractères (pré-enregistrement). */
export const VerdictPost = z.object({
  verdict: Verdict,
  rationale: z
    .string()
    .trim()
    .min(VERDICT_RATIONALE_MIN_LENGTH, { message: 'validation.rationaleTooShort' })
    .max(EXPERIMENT_TEXT_MAX_LENGTH),
});
export type VerdictPost = z.infer<typeof VerdictPost>;
/** Nom du corps côté SPA (fiche T27). */
export const VerdictBody = VerdictPost;
export type VerdictBody = VerdictPost;

/** `PUT /opportunities/:id/outcome` : upsert de `product_outcomes`. */
export const OutcomePut = z.object({
  buildStartedAt: IsoDate.nullable().optional(),
  launchedAt: IsoDate.nullable().optional(),
  buildDays: z.number().min(0).max(10_000).nullable().optional(),
  buildCostUsd: z.number().min(0).max(10_000_000).nullable().optional(),
  activation: z.array(OutcomeMetric).max(OUTCOME_METRICS_MAX).optional(),
  retention: z.array(OutcomeMetric).max(OUTCOME_METRICS_MAX).optional(),
  acquisition: z.array(OutcomeMetric).max(OUTCOME_METRICS_MAX).optional(),
  revenue: z.array(OutcomeMetric).max(OUTCOME_METRICS_MAX).optional(),
  notes: z.string().max(EXPERIMENT_NOTES_MAX_LENGTH).nullable().optional(),
});
export type OutcomePut = z.infer<typeof OutcomePut>;
/** Nom du corps côté SPA (fiche T27). */
export const OutcomeBody = OutcomePut;
export type OutcomeBody = OutcomePut;

/** Statuts d'opportunité depuis lesquels une expérience peut être préparée (`api.md`, T26). */
export const EXPERIMENT_CREATABLE_STATUSES = [
  'shortlisted',
  'experiment_planned',
  'experiment_running',
  'inconclusive',
] as const satisfies readonly OpportunityStatus[];

/** Statuts d'expérience qui occupent l'opportunité (une seule à la fois). */
export const EXPERIMENT_ACTIVE_STATUSES = ['planned', 'running'] as const;

/** Statuts d'opportunité qui acceptent un résultat produit (`PUT /opportunities/:id/outcome`). */
export const OUTCOME_EDITABLE_STATUSES = [
  'validated',
  'building',
  'launched',
  'archived',
] as const satisfies readonly OpportunityStatus[];
